import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Theme,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material';
import { useState } from 'react';
import { OnlyFansSubscriber } from '../../../../../../hooks/useSubscriber';
import { SubscribersAdmin } from '../../../../../../hooks/useSubscribers';
import StyledCardTitleBar from '../../../../../common/StyledCardTitleBar';
import SelectTimezone from '../../../../../forms/helpers/SelectTimezone';
import SextforceMetricsCompletedSelector from '../../SextforceMetricsCompletedSelector';
import SextforceMetricsCrossReferenceEarningsSubscriberSelector from '../../SextforceMetricsCrossReferenceEarningsSubscriberSelector';

interface SextforceMetricsCampaignsOverviewViewSettingsDialogProps {
    open: boolean;
    onClose: () => void;
    timezone: string;
    setTimezone: (timezone: string) => void;
    campaignsLoading: boolean;
    campaignsSelectorType: { includeInactive: boolean };
    setCampaignsSelectorType: (type: { includeInactive: boolean }) => void;
    completedType: boolean | null;
    setCompletedType: (type: boolean | null) => void;
    subscribers: SubscribersAdmin | OnlyFansSubscriber[] | undefined;
    subscribersLoading: boolean;
    crossReferenceSubscriberId: string;
    setCrossReferenceSubscriberId: (id: string) => void;
    amountType: 'gross' | 'net';
    setAmountType: (type: 'gross' | 'net') => void;
}

const SextforceMetricsCampaignsOverviewViewSettingsDialog = ({
    open,
    onClose,
    timezone,
    setTimezone,
    campaignsLoading,
    campaignsSelectorType,
    setCampaignsSelectorType,
    completedType,
    setCompletedType,
    subscribers,
    subscribersLoading,
    crossReferenceSubscriberId,
    setCrossReferenceSubscriberId,
    amountType,
    setAmountType,
}: SextforceMetricsCampaignsOverviewViewSettingsDialogProps) => {
    const theme: Theme = useTheme();

    // Local state for settings that will be applied when "Apply" is clicked
    const [localTimezone, setLocalTimezone] = useState(timezone);
    const [localCampaignsSelectorType, setLocalCampaingsSelectorType] = useState(campaignsSelectorType);
    const [localCompletedType, setLocalCompletedType] = useState(completedType);
    const [localCrossReferenceSubscriberId, setLocalCrossReferenceSubscriberId] = useState(crossReferenceSubscriberId);
    const [localAmountType, setLocalAmountType] = useState(amountType);

    // Reset local state when dialog opens
    const handleDialogEnter = () => {
        setLocalTimezone(timezone);
        setLocalCampaingsSelectorType(campaignsSelectorType);
        setLocalCompletedType(completedType);
        setLocalCrossReferenceSubscriberId(crossReferenceSubscriberId);
        setLocalAmountType(amountType);
    };

    // Apply settings and close dialog
    const handleApply = () => {
        setTimezone(localTimezone);
        setCampaignsSelectorType(localCampaignsSelectorType);
        setCompletedType(localCompletedType);
        setCrossReferenceSubscriberId(localCrossReferenceSubscriberId);

        setAmountType(localAmountType);
        localStorage.setItem('showEarningsAsGross', localAmountType === 'gross' ? 'true' : 'false');

        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            TransitionProps={{
                onEnter: handleDialogEnter,
            }}
        >
            <StyledCardTitleBar title="View Settings" />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SelectTimezone fullWidth size={'small'} timezone={localTimezone} setTimezone={setLocalTimezone} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Deleted Links
                        </Typography>
                        <ToggleButtonGroup
                            value={localCampaignsSelectorType.includeInactive ? 'true' : 'false'}
                            exclusive
                            fullWidth
                            color="secondary"
                            size={'small'}
                            disabled={campaignsLoading}
                            onChange={(e, value: string) => {
                                if (!value) {
                                    return;
                                }

                                setLocalCampaingsSelectorType({
                                    includeInactive: value === 'true',
                                });

                                localStorage.setItem('sextforceMetricsCampaignsOverviewSelectorType', value);
                            }}
                            sx={{ marginTop: '4px', height: '39px' }}
                        >
                            <ToggleButton color="secondary" fullWidth value={'false'} sx={{ minWidth: '120px' }}>
                                Hide Deleted
                            </ToggleButton>
                            <ToggleButton color="secondary" fullWidth value={'true'} sx={{ minWidth: '120px' }}>
                                Show Deleted
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Completed Promos
                        </Typography>
                        <SextforceMetricsCompletedSelector
                            loading={campaignsLoading}
                            completedType={localCompletedType}
                            setCompletedType={setLocalCompletedType}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Calculate Cross-Pollination With...
                        </Typography>
                        <SextforceMetricsCrossReferenceEarningsSubscriberSelector
                            subscribers={subscribers}
                            subscribersLoading={subscribersLoading}
                            crossReferenceSubscriberId={localCrossReferenceSubscriberId}
                            setCrossReferenceSubscriberId={setLocalCrossReferenceSubscriberId}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color={'text.secondary'}>
                            Amount Type
                        </Typography>
                        <ToggleButtonGroup
                            value={localAmountType}
                            exclusive
                            color="secondary"
                            size="small"
                            fullWidth
                            onChange={(_event, newValue) => {
                                if (!newValue) return;
                                setLocalAmountType(newValue);
                            }}
                            sx={{ height: '39px' }}
                        >
                            <ToggleButton value="gross" fullWidth sx={{ minWidth: '80px' }}>
                                GROSS
                            </ToggleButton>
                            <ToggleButton value="net" fullWidth sx={{ minWidth: '80px' }}>
                                NET
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                }}
            >
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleApply} color="primary" variant="contained">
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SextforceMetricsCampaignsOverviewViewSettingsDialog;
